import React, { useState, useCallback, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Turnstile } from '@marsidev/react-turnstile';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { personalInfo } from '../data/portfolio';
import { Mail, MapPin, Github, Linkedin, Send, CheckCircle, AlertCircle, RefreshCw, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useTheme } from '../context/ThemeContext';
import ParticleField from './ParticleField';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '1x00000000000000000000AA';

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.1, duration: 0.5, ease: 'easeOut' }
  })
};

// ── Captcha helpers ──────────────────────────────────────────────
const OPS = ['+', '−', '×'];
const rand = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;

const generateChallenge = () => {
  const op = OPS[rand(0, 2)];
  let a, b, answer;
  if (op === '+') { a = rand(2, 12); b = rand(2, 12); answer = a + b; }
  else if (op === '−') { a = rand(5, 15); b = rand(1, a); answer = a - b; }
  else { a = rand(2, 9); b = rand(2, 5); answer = a * b; }
  return { question: `${a} ${op} ${b}`, answer };
};

// ── Captcha component ────────────────────────────────────────────
const MathCaptcha = ({ onVerify }) => {
  const [challenge, setChallenge] = useState(generateChallenge);
  const [input, setInput] = useState('');
  const [status, setStatus] = useState('idle'); // idle | success | error

  const refresh = useCallback(() => {
    setChallenge(generateChallenge());
    setInput('');
    setStatus('idle');
    onVerify(false);
  }, [onVerify]);

  const check = (val) => {
    setInput(val);
    if (val === '') { setStatus('idle'); onVerify(false); return; }
    const correct = parseInt(val, 10) === challenge.answer;
    setStatus(correct ? 'success' : 'error');
    onVerify(correct);
  };

  // Auto-refresh on wrong after short delay
  useEffect(() => {
    if (status === 'error' && input.length >= String(challenge.answer).length + 1) {
      const t = setTimeout(refresh, 900);
      return () => clearTimeout(t);
    }
  }, [status, input, challenge, refresh]);

  const borderColor =
    status === 'success' ? 'hsl(var(--accent))' :
    status === 'error'   ? 'hsl(var(--destructive))' :
                           'hsl(var(--border))';

  return (
    <div
      className="rounded-xl p-4 flex items-center gap-3"
      style={{ background: 'hsl(var(--muted) / 0.4)', border: `1px solid ${borderColor}`, transition: 'border-color 0.2s' }}
    >
      {/* Shield icon */}
      <div
        className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
        style={{
          background: status === 'success' ? 'hsl(var(--accent) / 0.15)' : 'hsl(var(--primary) / 0.1)',
          border: `1px solid ${status === 'success' ? 'hsl(var(--accent) / 0.3)' : 'hsl(var(--primary) / 0.2)'}`,
        }}
      >
        {status === 'success'
          ? <CheckCircle size={16} style={{ color: 'hsl(var(--accent))' }} />
          : <ShieldCheck size={16} style={{ color: 'hsl(var(--primary))' }} />
        }
      </div>

      {/* Question */}
      <div className="flex-1 min-w-0">
        <p className="text-muted-foreground text-xs mb-1.5 font-mono">Verify you're human</p>
        <div className="flex items-center gap-2 flex-wrap">
          <span
            className="font-mono font-bold text-sm px-2.5 py-1.5 rounded-lg select-none flex-shrink-0"
            style={{ background: 'hsl(var(--surface-2))', color: 'hsl(var(--foreground))' }}
          >
            {challenge.question}
          </span>
          <div className="flex items-center gap-2 flex-shrink-0">
            =
            <Input
              type="number"
              value={input}
              onChange={e => check(e.target.value)}
              placeholder="?"
              className="w-16 text-center font-mono bg-muted/50 border-border/60 focus:border-primary/50 text-foreground placeholder:text-muted-foreground"
              style={{ borderColor }}
              autoComplete="off"
            />
            <AnimatePresence mode="wait">
              {status === 'success' && (
                <motion.span
                  key="ok"
                  initial={{ opacity: 0, scale: 0.5 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  style={{ color: 'hsl(var(--accent))' }}
                >
                  <CheckCircle size={16} />
                </motion.span>
              )}
              {status === 'error' && (
                <motion.span
                  key="err"
                  initial={{ opacity: 0, x: -4 }}
                  animate={{ opacity: 1, x: [0, -4, 4, -4, 4, 0] }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.4 }}
                  style={{ color: 'hsl(var(--destructive))' }}
                >
                  <AlertCircle size={16} />
                </motion.span>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Refresh */}
      <button
        type="button"
        onClick={refresh}
        title="New question"
        className="text-muted-foreground hover:text-primary transition-colors duration-200 flex-shrink-0"
      >
        <RefreshCw size={15} />
      </button>
    </div>
  );
};

// ── Main Section ─────────────────────────────────────────────────
const ContactSection = () => {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [captchaPassed, setCaptchaPassed] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');
  const turnstileRef = useRef(null);
  const { resolvedTheme } = useTheme();

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const resetForm = () => {
    setForm({ name: '', email: '', subject: '', message: '' });
    setCaptchaPassed(false);
    setTurnstileToken('');
    turnstileRef.current?.reset();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error('Please fill in Name, Email and Message.');
      return;
    }
    if (!captchaPassed) {
      toast.error('Please solve the math verification first.');
      return;
    }
    if (!turnstileToken) {
      toast.error('Please complete the security check.');
      return;
    }
    setSending(true);
    try {
      await axios.post(`${BACKEND_URL}/api/contact`, {
        ...form,
        turnstile_token: turnstileToken,
      });
      setSent(true);
      toast.success("Message sent! I'll get back to you shortly.");
      resetForm();
      setTimeout(() => setSent(false), 5000);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Something went wrong. Please try again.';
      toast.error(msg);
      turnstileRef.current?.reset();
      setTurnstileToken('');
    } finally {
      setSending(false);
    }
  };

  const contactDetails = [
    { icon: Mail, label: 'Email', value: personalInfo.email, href: `mailto:${personalInfo.email}?subject=Let's%20Connect%20Rajib&body=Hi%20Rajib,%0A%0AI%20came%20across%20your%20portfolio%20and%20would%20love%20to%20connect.%0A` },
    { icon: MapPin, label: 'Location', value: personalInfo.location, href: null },
    { icon: Github, label: 'GitHub', value: 'github.com/bboyrajib', href: personalInfo.github },
    { icon: Linkedin, label: 'LinkedIn', value: 'linkedin.com/in/rajib-roy', href: personalInfo.linkedin },
  ];

  return (
    <section id="contact" className="relative py-24 px-6 md:px-12 lg:px-24">
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: 'radial-gradient(ellipse at 30% 50%, hsl(var(--primary) / 0.05) 0%, transparent 60%)' }}
      />
      <ParticleField />
      <div className="max-w-7xl mx-auto">
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="mb-16 "
        >
          <Badge variant="outline" className="border-primary/30 text-primary bg-primary/5 mb-4 font-mono text-xs uppercase tracking-wider">
            Contact
          </Badge>
          <h2 className="font-heading font-bold text-4xl md:text-5xl text-foreground leading-tight">
            Let's Build
            <span className="gradient-text"> Something</span>
          </h2>
          <p className="text-muted-foreground mt-4 max-w-xl">
            Open to senior engineering roles, automation consulting, AI systems and collaborating on ambitious products.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-[1fr_1.4fr] gap-12">
          {/* Left: Info */}
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            custom={1}
            viewport={{ once: true }}
            className="space-y-8"
          >
            <div className="space-y-4">
              {contactDetails.map((item, i) => (
                <div key={i} className="flex items-center gap-4 py-2">
                  <div className="w-10 h-10 glass rounded-xl flex items-center justify-center flex-shrink-0">
                    <item.icon size={16} style={{ color: 'hsl(var(--primary))' }} />
                  </div>
                  <div className="flex-1 overflow-hidden">
                    <p className="text-muted-foreground text-xs mb-0.5">{item.label}</p>
                    {item.href ? (
                      <a
                        href={item.href}
                        target={item.href.startsWith('http') ? '_blank' : undefined}
                        rel={item.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                        className="text-sm font-medium hover:underline break-all"
                        style={{ color: 'hsl(var(--primary))' }}
                      >
                        {item.value}
                      </a>
                    ) : (
                      <p className="text-foreground text-sm font-medium break-words">{item.value}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Availability card */}
            <div className="glass rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-3">
                <span className="w-2 h-2 rounded-full bg-accent animate-pulse-glow" />
                <span className="text-foreground font-medium text-sm">Available for opportunities</span>
              </div>
              <p className="text-muted-foreground text-sm leading-relaxed">
                Currently open to senior engineering and automation roles, technical leadership positions,
                and consulting projects globally.
              </p>
              <div className="flex flex-wrap gap-2 mt-4">
                {['Remote OK', 'Toronto, ON', 'Automation', 'AI/ML', 'Tech Lead'].map((tag) => (
                  <span
                    key={tag}
                    className="text-xs font-mono px-3 py-1 rounded-full border border-accent/30 text-accent"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Right: Form */}
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            custom={2}
            viewport={{ once: true }}
          >
            <form onSubmit={handleSubmit} className="glass rounded-2xl p-8 space-y-5">
              <div className="grid sm:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <label className="text-foreground text-sm font-medium">Name *</label>
                  <Input
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    placeholder="Your name"
                    className="bg-muted/50 border-border/60 focus:border-primary/50 text-foreground placeholder:text-muted-foreground"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-foreground text-sm font-medium">Email *</label>
                  <Input
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="your@email.com"
                    className="bg-muted/50 border-border/60 focus:border-primary/50 text-foreground placeholder:text-muted-foreground"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-foreground text-sm font-medium">Subject</label>
                <Input
                  name="subject"
                  value={form.subject}
                  onChange={handleChange}
                  placeholder="What's this about?"
                  className="bg-muted/50 border-border/60 focus:border-primary/50 text-foreground placeholder:text-muted-foreground"
                />
              </div>

              <div className="space-y-2">
                <label className="text-foreground text-sm font-medium">Message *</label>
                <Textarea
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  placeholder="Tell me about the opportunity or project..."
                  rows={5}
                  className="bg-muted/50 border-border/60 focus:border-primary/50 text-foreground placeholder:text-muted-foreground resize-none"
                />
              </div>

              <MathCaptcha onVerify={setCaptchaPassed} />

              {/* Cloudflare Turnstile */}
              { captchaPassed &&
                <div className="rounded-xl overflow-hidden" style={{ margin: 'inherit', background: 'hsl(var(--muted) / 0.4)', border: '1px solid hsl(var(--border))', padding: '12px' }}>
                  <p className="text-muted-foreground text-xs font-mono mb-3 flex items-center gap-1.5">
                    <ShieldCheck size={12} style={{ color: 'hsl(var(--primary))' }} />
                    Security verification
                  </p>
                  {/* 🔥 CENTER FIX */}
                  <div className="flex justify-center">
                    <div className="w-full max-w-[320px]">
                      <Turnstile
                        ref={turnstileRef}
                        siteKey={TURNSTILE_SITE_KEY}
                        onSuccess={(token) => setTurnstileToken(token)}
                        onExpire={() => {
                          setTurnstileToken('');
                          turnstileRef.current?.reset();
                        }}
                        onError={() => {
                          setTurnstileToken('');
                          toast.error('Security check failed. Please try again.');
                        }}
                        options={{
                          theme: resolvedTheme === 'dark' ? 'dark' : 'light',
                          size: 'flexible',
                        }}
                      />
                    </div>
                  </div>
                </div>
              }

              <Button
                type="submit"
                size="lg"
                disabled={sending || sent || !captchaPassed || !turnstileToken}
                className="w-full font-semibold"
                style={{
                  background: sent
                    ? 'hsl(var(--accent))'
                    : (!captchaPassed || !turnstileToken)
                    ? 'hsl(var(--muted))'
                    : 'hsl(var(--primary))',
                  color: sent || (!captchaPassed || !turnstileToken)
                    ? 'hsl(var(--muted-foreground))'
                    : 'hsl(var(--primary-foreground))',
                  cursor: (!captchaPassed || !turnstileToken) ? 'not-allowed' : 'pointer',
                }}
              >
                {sent ? (
                  <><CheckCircle size={16} className="mr-2" /> Sent!</>
                ) : sending ? (
                  'Sending...'
                ) : (
                  <><Send size={16} className="mr-2" /> Send Message</>
                )}
              </Button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
